import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  booleanAttribute,
  numberAttribute,
} from '@angular/core';
import { DsIconComponent } from '../icons/ds-icon.component';
import { DsIconName } from '../icons/ds-icons';

export type DsHeaderButtonState = 'default' | 'selected' | 'hover' | 'focus';
export type DsHeaderButtonSize = 'm' | 's';
export type DsHeaderButtonNotification = 'none' | 'dot' | 'count';

@Component({
  selector: 'ds-header-button',
  standalone: true,
  imports: [DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      type="button"
      [class]="buttonClasses"
      [disabled]="disabled || null"
      [attr.aria-label]="resolvedAriaLabel"
      [attr.aria-pressed]="state === 'selected' ? 'true' : null"
      (click)="clicked.emit($event)"
    >
      <span class="ds-header-button__visual" aria-hidden="true">
        @if (icon) {
          <ds-icon [name]="icon" size="var(--ds-header-button-icon-size)" />
        } @else {
          <ng-content select="[dsItemButtonVisual]" />
        }
        @if (effectiveNotification !== 'none') {
          <span [class]="notificationClasses">
            @if (effectiveNotification === 'count') {
              {{ normalizedNotificationCount }}
            }
          </span>
        }
      </span>
      @if (!iconOnly) {
        <span class="ds-header-button__label">{{ label }}</span>
      }
    </button>
  `,
  styles: [`
    :host {
      display: inline-flex;
      flex: 0 0 auto;
    }

    .ds-header-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      border: 0;
      border-radius: var(--ds-header-button-border-radius);
      background: var(--ds-header-button-background);
      color: var(--ds-header-button-foreground);
      cursor: pointer;
      outline: none;
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-weight: var(--ds-header-button-font-weight);
      text-align: center;
      white-space: nowrap;
      transition:
        background-color var(--ds-header-button-transition-duration) ease-out,
        box-shadow var(--ds-header-button-transition-duration) ease-out,
        outline-color var(--ds-header-button-transition-duration) ease-out;
    }

    .ds-header-button--m {
      min-width: var(--ds-header-button-m-min-width);
      height: var(--ds-header-button-m-height);
      padding: var(--ds-header-button-m-padding-y) var(--ds-header-button-m-padding-x);
      gap: var(--ds-header-button-m-gap);
      font-size: var(--ds-header-button-m-font-size);
      line-height: var(--ds-header-button-m-line-height);
    }

    .ds-header-button--s {
      min-width: var(--ds-header-button-s-min-width);
      height: var(--ds-header-button-s-height);
      padding: var(--ds-header-button-s-padding-y) var(--ds-header-button-s-padding-x);
      gap: var(--ds-header-button-s-gap);
      font-size: var(--ds-header-button-s-font-size);
      line-height: var(--ds-header-button-s-line-height);
    }

    .ds-header-button__visual {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--ds-header-button-icon-size);
      height: var(--ds-header-button-icon-size);
      flex: 0 0 auto;
    }

    .ds-header-button__notification {
      position: absolute;
      z-index: 1;
      inset-block-start: var(--ds-header-button-notification-offset-top);
      inset-inline-end: var(--ds-header-button-notification-offset-end);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      border-radius: var(--ds-header-button-notification-border-radius);
      background: var(--ds-header-button-notification-background);
      color: var(--ds-header-button-notification-foreground);
      pointer-events: none;
      animation: ds-header-button-notification-enter
        var(--ds-header-button-notification-transition-duration)
        ease-out
        both;
    }

    .ds-header-button__notification--dot {
      width: var(--ds-header-button-notification-dot-size);
      height: var(--ds-header-button-notification-dot-size);
    }

    .ds-header-button__notification--count {
      min-width: var(--ds-header-button-notification-count-min-width);
      height: var(--ds-header-button-notification-count-height);
      padding: 0 var(--ds-header-button-notification-count-padding-x);
      font-size: var(--ds-header-button-notification-font-size);
      font-weight: var(--ds-header-button-notification-font-weight);
      line-height: var(--ds-header-button-notification-line-height);
      white-space: nowrap;
    }

    @keyframes ds-header-button-notification-enter {
      0% {
        opacity: 0;
        transform: translateY(var(--ds-header-button-notification-animation-start-offset-y));
      }

      70% {
        opacity: 1;
        transform: translateY(var(--ds-header-button-notification-animation-settle-offset-y));
      }

      100% {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .ds-header-button__notification {
        animation: none;
      }
    }

    .ds-header-button__label {
      display: block;
      text-box-edge: cap alphabetic;
      text-box-trim: trim-both;
    }

    .ds-header-button:hover:not(:disabled):not(.ds-header-button--selected):not(.ds-header-button--focus):not(:focus-visible),
    .ds-header-button--hover:not(:disabled) {
      background: var(--ds-header-button-hover-background);
      box-shadow: none;
    }

    .ds-header-button--selected:not(:disabled),
    .ds-header-button--focus:not(:disabled),
    .ds-header-button:focus-visible:not(:disabled) {
      background: var(--ds-header-button-background);
      box-shadow: var(--ds-header-button-state-shadow);
    }

    .ds-header-button:focus-visible,
    .ds-header-button--focus {
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-header-button:disabled {
      cursor: not-allowed;
    }

    /* Foundation breakpoints applied to the containing Header's inline size. */
    @container ds-header (max-width: 1140px) {
      :host(.ds-header__responsive-action) .ds-header-button__label {
        display: none;
      }
    }

    @container ds-header (max-width: 839px) {
      :host(.ds-header__responsive-action) .ds-header-button--m {
        min-width: var(--ds-header-button-s-min-width);
        height: var(--ds-header-button-s-height);
        padding: var(--ds-header-button-s-padding-y) var(--ds-header-button-s-padding-x);
        gap: var(--ds-header-button-s-gap);
        font-size: var(--ds-header-button-s-font-size);
        line-height: var(--ds-header-button-s-line-height);
      }
    }
  `],
})
export class DsHeaderButtonComponent {
  @Input() label = 'Header Button';
  @Input() ariaLabel = '';
  @Input() icon: DsIconName | string = 'Add';
  @Input() size: DsHeaderButtonSize = 'm';
  @Input() state: DsHeaderButtonState = 'default';
  @Input() notification: DsHeaderButtonNotification = 'none';
  @Input({ transform: numberAttribute }) notificationCount = 0;
  @Input() notificationAriaLabel = '';
  @Input({ transform: booleanAttribute }) iconOnly = false;
  @Input({ transform: booleanAttribute }) disabled = false;

  @Output() clicked = new EventEmitter<MouseEvent>();

  get buttonClasses(): string {
    return `ds-header-button ds-header-button--${this.size} ds-header-button--${this.state}`;
  }

  get notificationClasses(): string {
    return `ds-header-button__notification ds-header-button__notification--${this.effectiveNotification}`;
  }

  get normalizedNotificationCount(): number {
    if (!Number.isFinite(this.notificationCount)) return 0;
    return Math.max(0, Math.trunc(this.notificationCount));
  }

  get effectiveNotification(): DsHeaderButtonNotification {
    if (this.notification === 'count' && this.normalizedNotificationCount === 0) {
      return 'none';
    }
    return this.notification;
  }

  get resolvedAriaLabel(): string {
    const label = this.ariaLabel || this.label;
    if (this.effectiveNotification === 'none') return label;

    const notificationLabel = this.notificationAriaLabel || (
      this.effectiveNotification === 'dot'
        ? 'Tiene novedades'
        : this.normalizedNotificationCount === 1
          ? '1 novedad'
          : `${this.normalizedNotificationCount} novedades`
    );

    return `${label}. ${notificationLabel}`;
  }
}
